import React, {useCallback, useMemo, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {useQuery} from "@tanstack/react-query";
import {FaPen, FaPlus} from "react-icons/fa";
import {toast} from "react-toastify";
import Button from "../../../components/button";
import Modal from "../../../components/modal";
import InputForm from "../../../components/input-form";
import SelectComponent from "../../../components/select-component";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import {AlertMessage} from "../../../utils/alert-message";
import {formatSalary, MaskDefault, parseSalary} from "../../../utils/mask";
import {cvBenefitService} from "../../../services/curriculum-resource-service";

const initialBenefit = {
    benefit: null,
    monthly_value: "",
};

const BenefitEditButton = ({api, node, openEdit}) => (
    <div className="flex justify-center h-full w-full">
        <button type="button" onClick={() => openEdit(api, node)}
                className="w-[60px] h-full flex justify-center items-center text-gray-900 bg-gray-50 shadow border border-gray-300 cursor-pointer rounded-md me-[6px]"
                title="Editar benefício">
            <FaPen/>
        </button>
    </div>
);

const BenefitsSection = ({benefits, gridRef, onBenefitsChange, onTotalChange, totalRevenue, disabled}) => {
    const [open, setOpen] = useState(false);
    const [newRow, setNewRow] = useState(false);
    const [benefit, setBenefit] = useState(initialBenefit);
    const [modalApi, setModalApi] = useState(null);
    const [modalNode, setModalNode] = useState(null);
    const [total, setTotal] = useState(() => (
        benefits.reduce((sum, item) => sum + parseSalary(item.monthly_value), 0)
    ));

    const {data: benefitOptions = [], isFetching} = useQuery({
        queryFn: () => cvBenefitService.options().then(({data}) => (
            data.map(({id, name}) => ({value: id, label: name}))
        )),
        queryKey: ["cv-benefits-options"],
    });

    const closeModal = useCallback(() => {
        setOpen(false);
        setNewRow(false);
        setBenefit(initialBenefit);
        setModalApi(null);
        setModalNode(null);
    }, []);

    const openEdit = useCallback((api, node) => {
        const selectedBenefit = benefitOptions.find(({value}) => value === node.data.benefit_id) ?? {
            value: node.data.benefit_id,
            label: node.data.benefit_name,
        };

        setBenefit({
            benefit: selectedBenefit,
            monthly_value: node.data.monthly_value,
        });
        setModalApi(api);
        setModalNode(node);
        setNewRow(false);
        setOpen(true);
    }, [benefitOptions]);

    const columnDefs = useMemo(() => [
        {field: "benefit_name", headerName: "Benefício", flex: 1},
        {field: "monthly_value", headerName: "Valor/mês", flex: 1},
        {
            field: "",
            headerName: "",
            sortable: false,
            editable: false,
            width: 100,
            cellRendererSelector: ({node}) => node.rowPinned || disabled ? undefined : ({
                component: BenefitEditButton,
                params: {openEdit},
            }),
        },
    ], [disabled, openEdit]);

    const defaultColDef = useMemo(() => ({resizable: false, editable: false}), []);
    const updateTotal = useCallback((api) => {
        let value = 0;
        api.forEachNode(({data}) => {
            value += parseSalary(data.monthly_value);
        });
        setTotal(value);
        onTotalChange(value);
    }, [onTotalChange]);

    const syncBenefits = useCallback((api) => {
        const rows = [];
        api.forEachNode(({data}) => rows.push(data));
        onBenefitsChange(rows);
        updateTotal(api);
    }, [onBenefitsChange, updateTotal]);

    const openCreate = () => {
        setBenefit(initialBenefit);
        setNewRow(true);
        setOpen(true);
    };

    const save = () => {
        if (!benefit.benefit || !benefit.monthly_value) {
            toast.error(AlertMessage.generalEmpty);
            return;
        }

        const row = {
            benefit_id: benefit.benefit.value,
            benefit_name: benefit.benefit.label,
            monthly_value: benefit.monthly_value,
        };

        if (newRow) {
            gridRef.current?.api.applyTransaction({add: [row]});
        } else {
            modalNode.setData(row);
        }

        syncBenefits(gridRef.current.api);

        closeModal();
    };

    const remove = () => {
        modalApi.applyTransaction({remove: [modalNode.data]});
        syncBenefits(modalApi);
        closeModal();
    };

    return (
        <div>
            {!disabled && (
                <div className="flex justify-end mb-3">
                    <Button onClick={openCreate} target="primary">
                        <FaPlus/>
                    </Button>
                </div>
            )}

            <div className="ag-theme-quartz grid h-[230px]">
                <AgGridReact
                    ref={gridRef}
                    rowHeight={32}
                    rowData={benefits}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    animateRows={false}
                    suppressRowClickSelection={true}
                    suppressCellFocus={true}
                    gridOptions={{localeText: LocalizationAgGrid}}
                    onGridReady={({api}) => updateTotal(api)}
                    onRowDataUpdated={({api}) => updateTotal(api)}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-sm text-gray-500">Total dos benefícios</p>
                    <strong className="block mt-1 text-lg text-gray-800">{formatSalary(total)}</strong>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                    <p className="text-sm text-blue-700">Total estimado de ganhos</p>
                    <strong className="block mt-1 text-lg text-blue-900">{totalRevenue}</strong>
                </div>
            </div>

            <Modal isOpen={open} onClose={closeModal} title="Benefício" overflow className="w-11/12 lg:w-6/12">
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12 m-5">
                    <SelectComponent
                        instanceId="candidate_benefit"
                        name="candidate_benefit"
                        label="Benefício *"
                        options={benefitOptions}
                        value={benefit.benefit}
                        onChange={(value) => setBenefit(prev => ({...prev, benefit: value}))}
                        isLoading={isFetching}
                        col="col-span-7"
                    />
                    <InputForm
                        id="monthly_value"
                        name="monthly_value"
                        label="Valor/mês *"
                        value={benefit.monthly_value}
                        onChange={({target: {value}}) => setBenefit(prev => ({...prev, monthly_value: value}))}
                        mask={MaskDefault.salary}
                        col="col-span-5"
                    />
                </div>

                <div className="flex justify-end gap-3 px-3 py-3 border-t border-gray-300 bg-[#F2F2F2] rounded-b-lg">
                    {!newRow && (
                        <Button target="danger" onClick={remove}>Excluir</Button>
                    )}
                    <Button target="success" onClick={save}>Confirmar</Button>
                </div>
            </Modal>
        </div>
    );
};

export default BenefitsSection;
